class ProjectGallery extends HTMLElement {
  constructor() {
    super();
    this.batchSize = this.getBatchSize();
    this.btnEventListeners();

    try {
      this.setupInfiniteScroll();
    } catch (err) {
      console.error('[project-gallery] Failed to set up infinite scroll, falling back to scroll listener only:', err);
      this.setupScrollFallback();
    }
  }

  getBatchSize() {
    const firstHidden = this.querySelector('.gallery-item[hidden]');
    const allItems = this.querySelectorAll('.gallery-item');
    if (!firstHidden) return allItems.length || 6;
    const index = Array.from(allItems).indexOf(firstHidden);
    return index > 0 ? index : 6;
  }

  btnEventListeners() {
    const { switchButton } = this.galleryComponents();

    switchButton.forEach(galleryButton => {
      galleryButton.addEventListener('click', (event) => {
        this.switchGalleryImage(event.currentTarget);
      })
    })
  }

  setupInfiniteScroll() {
    const sentinel = this.querySelector('.gallery-sentinel');
    if (!sentinel) return;

    if (!('IntersectionObserver' in window)) {
      this.setupScrollFallback();
      return;
    }

    this.scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.revealNextBatch();
        }
      });
    }, { rootMargin: '400px 0px' });

    this.scrollObserver.observe(sentinel);

    // Belt-and-braces: some themes fake scrolling with a transformed wrapper
    // (Lenis, Locomotive Scroll, GSAP ScrollSmoother, etc.), which breaks the
    // observer's default viewport-based root. This fallback catches that case
    // without needing to know which library, if any, is in use.
    this.setupScrollFallback();
  }

  setupScrollFallback() {
    if (this._scrollFallbackBound) return;
    this._scrollFallbackBound = true;

    this._onScrollCheck = () => {
      if (this._ticking) return;
      this._ticking = true;
      requestAnimationFrame(() => {
        this.checkSentinelPosition();
        this._ticking = false;
      });
    };

    window.addEventListener('scroll', this._onScrollCheck, { passive: true });
    window.addEventListener('resize', this._onScrollCheck, { passive: true });
    // Run once immediately in case content is short enough to already need a reveal.
    this._onScrollCheck();
  }

  checkSentinelPosition() {
    const sentinel = this.querySelector('.gallery-sentinel');
    if (!sentinel) return;

    const rect = sentinel.getBoundingClientRect();
    const nearViewport = rect.top < window.innerHeight + 400;

    if (nearViewport) {
      this.revealNextBatch();
    }
  }

  revealNextBatch() {
    const hiddenItems = this.querySelectorAll('.gallery-item[hidden]');

    if (hiddenItems.length === 0) {
      this.teardownScrollListeners();
      return;
    }

    Array.from(hiddenItems)
      .slice(0, this.batchSize)
      .forEach(item => item.removeAttribute('hidden'));

    if (this.querySelectorAll('.gallery-item[hidden]').length === 0) {
      this.teardownScrollListeners();
    }
  }

  teardownScrollListeners() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
    if (this._onScrollCheck) {
      window.removeEventListener('scroll', this._onScrollCheck);
      window.removeEventListener('resize', this._onScrollCheck);
    }
  }

  switchGalleryImage(button) {
    const parentElement = button.parentElement.parentElement;
    const afterImage = parentElement.querySelector('.after-image');
    const beforeImage = parentElement.querySelector('.before-image');
    const label = parentElement.querySelector('.project-label');

    if(beforeImage.classList.contains('hidden')) {
      beforeImage.classList.replace('hidden', 'block');
      afterImage.classList.replace('block', 'hidden');

      label.textContent = 'Before';
      button.textContent = 'Show after';
    } else {
      afterImage.classList.replace('hidden', 'block');
      beforeImage.classList.replace('block', 'hidden');

      label.textContent = 'After';
      button.textContent = 'Show before';
    }
  }

  galleryComponents() {
    const switchButton = this.querySelectorAll('.switch-button');

    return { switchButton }
  }
}

customElements.define('project-gallery', ProjectGallery);